/**
 * FinishReasonModal — prompts the user for a reason code before finishing
 * an emergency alert via the /emergency/finish endpoint.
 *
 * Props:
 *   row          — the alarm row being finished (used for header context)
 *   reasonCodes  — campus-specific cloud reason codes: [{cloud_reason_code_id, reason_code_description, pal_reason_description}]
 *   saving       — true while the finish request is in flight (disables buttons, shows spinner)
 *   onSubmit     — (reasonId:number) => void, called when user confirms a reason
 *   onCancel     — () => void, called when user dismisses
 */

import { useEffect, useMemo, useState } from 'react';

const FinishReasonModal = ({ row, reasonCodes, saving, onSubmit, onCancel }) => {
    const [reasonId, setReasonId] = useState('');
    const [error, setError] = useState('');

    // Close on Escape
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !saving) onCancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [saving, onCancel]);

    /** Sort + group reason codes by category for the dropdown. */
    const grouped = useMemo(() => {
        const byCategory = new Map();
        (reasonCodes || []).forEach((rc) => {
            const cat = rc.pal_reason_description || 'Other';
            if (!byCategory.has(cat)) byCategory.set(cat, []);
            byCategory.get(cat).push(rc);
        });
        const categories = [...byCategory.keys()].sort((a, b) => a.localeCompare(b));
        return categories.map((cat) => ({
            category: cat,
            items: byCategory.get(cat).sort((a, b) =>
                (a.reason_code_description || '').localeCompare(b.reason_code_description || '')
            ),
        }));
    }, [reasonCodes]);

    const handleSubmit = () => {
        const id = parseInt(reasonId, 10);
        if (!id) {
            setError('Please select a reason.');
            return;
        }
        onSubmit(id);
    };

    return (
        <div className="pp-dialog-backdrop" onClick={() => { if (!saving) onCancel(); }}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Finish Alert">
                <div className="pp-dialog__header">
                    <h2 className="pp-dialog__title">Finish Alert</h2>
                    <button className="dp-drawer__close" onClick={onCancel} disabled={saving} aria-label="Close">{'\u2715'}</button>
                </div>
                <div className="pp-dialog__body">
                    <p style={{ margin: '0 0 var(--space-3, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                        Select a reason to finish{' '}
                        <strong style={{ color: 'var(--color-text-body)' }}>
                            {row?.event_type || row?.event_category || 'this alert'}
                        </strong>
                        {row?.resident_name ? (
                            <> for <strong style={{ color: 'var(--color-text-body)' }}>{row.resident_name}</strong></>
                        ) : row?.area_name ? (
                            <> in <strong style={{ color: 'var(--color-text-body)' }}>{row.area_name}</strong></>
                        ) : null}
                        .
                    </p>

                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label" htmlFor="finish-reason-select">
                            Reason <span className="dp-drawer__required">*</span>
                        </label>
                        <select
                            id="finish-reason-select"
                            className={`dp-drawer__input${error ? ' is-invalid' : ''}`}
                            value={reasonId}
                            disabled={saving}
                            onChange={(e) => {
                                setReasonId(e.target.value);
                                if (error) setError('');
                            }}
                        >
                            <option value="">-- Select a reason --</option>
                            {grouped.length === 0 ? (
                                <option value="" disabled>No reason codes configured for this campus</option>
                            ) : null}
                            {grouped.map((g) => (
                                <optgroup key={g.category} label={g.category}>
                                    {g.items.map((rc) => (
                                        <option key={rc.cloud_reason_code_id} value={rc.cloud_reason_code_id}>
                                            {rc.reason_code_description}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        {error ? <p className="dp-drawer__error">{error}</p> : null}
                    </div>
                </div>
                <div className="pp-dialog__footer">
                    <button
                        type="button"
                        className="dp-btn dp-btn--cancel"
                        onClick={onCancel}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="dp-btn na-action-btn--finish"
                        onClick={handleSubmit}
                        disabled={saving || !reasonId}
                    >
                        {saving ? 'Finishing\u2026' : 'Finish Alert'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FinishReasonModal;
